import {
  ACHPaymentInfoDTO,
  ACHPaymentInfoSchema,
  ACHPaymentInfoYupSchema,
} from 'shared/modules/carrier-profile/data/CarrierProfileDTO';
import { useMemo, useState } from 'react';
import { useSendACHPaymentInfoVerificationCode } from 'shared/modules/carrier-profile/data/CarrierProfileAPI';
import { useSnackbarStack } from '@superdispatch/ui';
import { useInitialValues } from 'shared/form/AppFormik';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import { useFlaggedSchema } from 'shared/hooks/useFlaggedSchema';
import { useFormikEnhanced } from '@superdispatch/forms';
import { APIResponse } from 'shared/api/CarrierAPIClient';

export function useACHPaymentInfoForm({
  paymentInfo,
  onSuccess,
}: {
  paymentInfo?: ACHPaymentInfoDTO;
  onSuccess: () => void;
}) {
  const [updatedPaymentInfo, setUpdatedPaymentInfo] =
    useState<ACHPaymentInfoDTO>();
  const { mutateAsync: sendCode } = useSendACHPaymentInfoVerificationCode();
  const { addSnackbar } = useSnackbarStack();
  const schema = useFlaggedSchema(
    'ctms_zod_schema',
    ACHPaymentInfoYupSchema,
    ACHPaymentInfoSchema,
  );
  const initialValues = useInitialValues(schema, paymentInfo);
  const validationSchema = useMemo(
    () => toFormikValidationSchema(schema),
    [schema],
  );

  const formik = useFormikEnhanced<ACHPaymentInfoDTO, APIResponse>({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      setUpdatedPaymentInfo(values);
      return sendCode('email');
    },
    onSubmitSuccess: () => {
      onSuccess();
    },
    onSubmitFailure: ({ message }) =>
      addSnackbar(`Failed to update ACH payment info: ${message}`, {
        variant: 'error',
      }),
  });

  return {
    formik,
    updatedPaymentInfo,
    setUpdatedPaymentInfo,
  };
}
